"use client"

import { Head } from "@inertiajs/react"
import {
  Check,
  Copy01,
  Download01,
  Pin01,
  Shuffle01,
  XClose,
} from "@untitledui/icons"
import { useCallback, useMemo, useState } from "react"

import {
  FORM_HELP,
  SCREEN_HELP,
  SECTION_HELP,
  SLIDER_HELP,
} from "@/components/admin/cover-press/help-copy"
import { HelpHeading, HelpTip } from "@/components/admin/cover-press/help-tip"
import { InSitu } from "@/components/admin/cover-press/in-situ"
import { PressCanvas } from "@/components/admin/cover-press/press-canvas"
import { RecipeSlider } from "@/components/admin/cover-press/recipe-slider"
import { Stage, type ZoomKey } from "@/components/admin/cover-press/stage"
import { Button } from "@/components/ui/button"
import { ButtonUtility } from "@/components/ui/button-utility"
import { Input } from "@/components/ui/input"
import { Tooltip } from "@/components/ui/tooltip"
import { DashboardLayout } from "@/layouts/dashboard-layout"
import {
  ARCHETYPES,
  type ArchetypeKey,
  FORMATS,
  FORMS,
  type FormKey,
  type Params,
  type Recipe,
  type RecipeParams,
  SCREENS,
  type ScreenKey,
  WAYS,
  type WayKey,
  applyArchetype,
  paintRecipe,
  permsFor,
  recipeFilename,
  recipeFromParams,
  recipeLabel,
  recipeToQuery,
  seriesVariant,
} from "@/lib/cover-press/engine"
import { adminCoverPressPath } from "@/routes"

interface CoverPressProps {
  /** Flat recipe params from a copied studio link, or null for defaults. */
  recipe: RecipeParams | null
}

interface Pin {
  id: number
  recipe: Recipe
}

/** Pinned proofs survive a reload; they live only in this browser. */
const PINS_STORAGE_KEY = "cover-press:pins"

const SERIES = [1, 2, 3, 4, 5, 6]
/** Secondary pulls wait a beat so the hero stays responsive while dragging. */
const SETTLE_MS = 350

/**
 * The studio fills the viewport under the dashboard header on large screens
 * so the proof and the recipe scroll independently. Keep in step with
 * DashboardHeader's rendered height.
 */
const STUDIO_HEIGHT = "lg:h-[calc(100dvh-4.25rem)]"

const FOCAL_LABELS = [
  "top left",
  "top centre",
  "top right",
  "middle left",
  "centre",
  "middle right",
  "bottom left",
  "bottom centre",
  "bottom right",
]

function isStoredPin(v: unknown): v is { id: number; recipe: RecipeParams } {
  return (
    typeof v === "object" &&
    v !== null &&
    typeof (v as { id?: unknown }).id === "number"
  )
}

function loadPins(): Pin[] {
  if (typeof window === "undefined") return []
  try {
    const raw = window.localStorage.getItem(PINS_STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(isStoredPin)
      .map((p) => ({ id: p.id, recipe: recipeFromParams(p.recipe) }))
  } catch {
    return []
  }
}

function savePins(pins: Pin[]) {
  try {
    window.localStorage.setItem(PINS_STORAGE_KEY, JSON.stringify(pins))
  } catch {
    // Storage blocked or full: pins simply won't survive a reload.
  }
}

export default function CoverPress({ recipe: initial }: CoverPressProps) {
  const [recipe, setRecipe] = useState<Recipe>(() => recipeFromParams(initial))
  const [zoom, setZoom] = useState<ZoomKey>("fit")
  const [pins, setPins] = useState<Pin[]>(loadPins)
  const [copied, setCopied] = useState(false)
  const [exporting, setExporting] = useState(false)

  const setP = useCallback(
    <K extends keyof Params>(key: K, value: Params[K]) =>
      setRecipe((r) => ({ ...r, p: { ...r.p, [key]: value } })),
    [],
  )

  const perms = permsFor(WAYS[recipe.way])
  const format = FORMATS[recipe.format]
  const series = useMemo(
    () => SERIES.map((i) => seriesVariant(recipe, i)),
    [recipe],
  )

  const reseed = () =>
    setRecipe((r) => ({ ...r, seed: Math.floor(Math.random() * 99999) }))

  const pin = () => {
    const next = [...pins, { id: Date.now(), recipe }]
    setPins(next)
    savePins(next)
  }

  const unpin = (id: number) => {
    const next = pins.filter((p) => p.id !== id)
    setPins(next)
    savePins(next)
  }

  const copyLink = () => {
    const url = `${window.location.origin}${adminCoverPressPath()}?${recipeToQuery(recipe)}`
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  // The full-size pull is CPU-bound (a 1600² cover is 2.5M pixels), so the
  // button shows its busy state for a frame before the paint blocks the thread.
  const exportPng = () => {
    setExporting(true)
    setTimeout(() => {
      const cv = document.createElement("canvas")
      paintRecipe(cv, recipe, format.w, format.h, true)
      cv.toBlob((blob) => {
        setExporting(false)
        if (!blob) return
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = recipeFilename(recipe)
        a.click()
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      }, "image/png")
    }, 30)
  }

  return (
    <DashboardLayout>
      <Head title="Cover Press" />
      <div
        className={`flex w-full flex-col gap-4 px-4 pt-5 pb-10 lg:px-8 lg:pb-0 ${STUDIO_HEIGHT}`}
      >
        <header className="flex shrink-0 items-baseline gap-4">
          <h1 className="text-primary font-display md:text-display-xs text-xl font-semibold">
            Cover Press
          </h1>
          <span className="text-tertiary text-sm tabular-nums">
            {recipeLabel(recipe)}
          </span>
        </header>

        <div className="grid min-h-0 grid-cols-1 gap-x-8 gap-y-10 lg:flex-1 lg:grid-cols-[minmax(0,1fr)_22rem]">
          {/* ---- Left: the pull fills the first screen; the whole column scrolls to its outputs ---- */}
          <div className="flex min-h-0 flex-col gap-6 lg:overflow-y-auto lg:pr-1 lg:pb-8">
            <section className="flex min-h-0 shrink-0 flex-col gap-4 lg:h-full">
              <div className="flex min-h-0 flex-col max-lg:h-[min(80vw,560px)] lg:flex-1">
                <Stage
                  recipe={recipe}
                  zoom={zoom}
                  onZoomChange={setZoom}
                  onFormatChange={(f) =>
                    setRecipe((r) => ({ ...r, format: f }))
                  }
                />
              </div>

              <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
                <div className="w-24">
                  <Input
                    aria-label="Seed"
                    placeholder="Seed"
                    type="number"
                    size="sm"
                    value={String(recipe.seed)}
                    onChange={(v) =>
                      setRecipe((r) => ({
                        ...r,
                        seed: Math.max(0, parseInt(v, 10) || 0),
                      }))
                    }
                  />
                </div>
                <ButtonUtility
                  size="sm"
                  color="secondary"
                  icon={Shuffle01}
                  tooltip="New seed"
                  onClick={reseed}
                />
                <HelpTip
                  label="Seed"
                  help={SECTION_HELP.seed}
                  placement="top"
                />
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    color="secondary"
                    iconLeading={Pin01}
                    onClick={pin}
                  >
                    Pin proof
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    color="secondary"
                    iconLeading={copied ? Check : Copy01}
                    onClick={copyLink}
                  >
                    {copied ? "Copied" : "Copy link"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    color="primary"
                    iconLeading={Download01}
                    isLoading={exporting}
                    onClick={exportPng}
                  >
                    Export PNG
                  </Button>
                </div>
              </div>
            </section>

            <div className="flex shrink-0 flex-col gap-8">
              <section className="flex flex-col gap-3">
                <HelpHeading
                  title="Series · sibling pressings"
                  help={SECTION_HELP.series}
                  placement="top"
                />
                <ul className="m-0 flex list-none flex-wrap gap-3 p-0">
                  {series.map((sv, idx) => (
                    <li key={SERIES[idx]}>
                      <button
                        type="button"
                        onClick={() => setRecipe(sv)}
                        aria-label={`Adopt sibling pressing, seed ${sv.seed}`}
                        className="outline-focus-ring flex flex-col gap-1.5 rounded-md focus-visible:outline-2 focus-visible:outline-offset-2"
                      >
                        <PressCanvas
                          recipe={sv}
                          width={120}
                          height={120}
                          delayMs={SETTLE_MS}
                          className="block size-[104px] rounded"
                          aria-hidden
                        />
                        <span className="text-tertiary text-left text-xs tabular-nums">
                          {sv.seed}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="flex flex-col gap-4">
                <HelpHeading
                  title="In situ · presentation layers"
                  help={SECTION_HELP.inSitu}
                  placement="top"
                />
                <InSitu recipe={recipe} />
              </section>

              {pins.length > 0 && (
                <section className="flex flex-col gap-3">
                  <HelpHeading
                    title="Pinned proofs"
                    help={SECTION_HELP.pins}
                    placement="top"
                  />
                  <ul className="m-0 flex list-none flex-wrap gap-4 p-0">
                    {pins.map((pn) => (
                      <li
                        key={pn.id}
                        className="flex w-[110px] flex-col gap-1.5"
                      >
                        <button
                          type="button"
                          onClick={() => setRecipe(pn.recipe)}
                          aria-label={`Restore proof ${recipeLabel(pn.recipe)}`}
                          className="outline-focus-ring rounded-md focus-visible:outline-2 focus-visible:outline-offset-2"
                        >
                          <PressCanvas
                            recipe={pn.recipe}
                            width={110}
                            height={110}
                            delayMs={SETTLE_MS}
                            className="block size-[110px] rounded shadow-xs"
                            aria-hidden
                          />
                        </button>
                        <div className="flex items-center justify-between gap-1">
                          <span className="text-tertiary truncate text-xs tabular-nums">
                            {recipeLabel(pn.recipe)}
                          </span>
                          <ButtonUtility
                            size="xs"
                            color="tertiary"
                            icon={XClose}
                            tooltip="Remove pin"
                            onClick={() => unpin(pn.id)}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          </div>

          {/* ---- Right: the recipe, scrolling on its own ---- */}
          <div className="border-secondary flex min-h-0 flex-col gap-6 lg:overflow-y-auto lg:border-l lg:pr-1 lg:pb-8 lg:pl-6">
            <section className="flex flex-col gap-3">
              <HelpHeading title="Start from" help={SECTION_HELP.start} />
              <div className="flex flex-wrap gap-1.5">
                {(Object.keys(ARCHETYPES) as ArchetypeKey[]).map((key) => (
                  <Button
                    key={key}
                    type="button"
                    size="sm"
                    color="secondary"
                    onClick={() => setRecipe((r) => applyArchetype(r, key))}
                  >
                    {ARCHETYPES[key].label}
                  </Button>
                ))}
              </div>
            </section>

            <section className="border-secondary flex flex-col gap-3 border-t pt-5">
              <HelpHeading
                title="Colourway · paper + ink stack"
                help={SECTION_HELP.colourway}
              />
              <div className="grid grid-cols-2 gap-2">
                {(Object.keys(WAYS) as WayKey[]).map((k) => {
                  const w = WAYS[k]
                  const active = k === recipe.way
                  return (
                    <Button
                      key={k}
                      type="button"
                      size="sm"
                      color={active ? "secondary" : "tertiary"}
                      aria-pressed={active}
                      onClick={() =>
                        setRecipe((r) => ({ ...r, way: k, perm: 0 }))
                      }
                      className="h-auto min-w-0 justify-start px-2 py-1.5 text-left"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <InkSwatch
                          paper={w.paper}
                          inks={w.inks.map((r) => r[0])}
                        />
                        <span className="flex min-w-0 flex-col">
                          <span className="text-secondary text-xs leading-tight font-semibold whitespace-normal">
                            {w.name}
                          </span>
                          <span className="text-quaternary text-[11px] leading-tight font-normal whitespace-normal">
                            {w.ref}
                          </span>
                        </span>
                      </span>
                    </Button>
                  )
                })}
              </div>

              {perms && (
                <div className="flex flex-col gap-2">
                  <HelpHeading
                    title="Plate order"
                    help={SECTION_HELP.plateOrder}
                    size="xs"
                  />
                  <div
                    role="group"
                    aria-label="Plate order"
                    className="flex flex-wrap gap-1.5"
                  >
                    {perms.map((pm, idx) => {
                      const active = recipe.perm % perms.length === idx
                      return (
                        <Button
                          key={idx}
                          type="button"
                          size="sm"
                          color={active ? "secondary" : "tertiary"}
                          aria-pressed={active}
                          aria-label={`Plate order ${idx + 1}`}
                          onClick={() =>
                            setRecipe((r) => ({ ...r, perm: idx }))
                          }
                        >
                          <span className="flex items-center gap-1">
                            {pm.map((i) => (
                              <span
                                key={i}
                                className="size-3 rounded-full"
                                style={{
                                  background: WAYS[recipe.way].inks[i][0],
                                  boxShadow: `0 0 0 1.5px ${WAYS[recipe.way].paper}`,
                                }}
                              />
                            ))}
                          </span>
                        </Button>
                      )
                    })}
                  </div>
                </div>
              )}
            </section>

            <section className="border-secondary flex flex-col gap-4 border-t pt-5">
              <HelpHeading title="Form" help={SECTION_HELP.form} />
              <div
                role="group"
                aria-label="Form"
                className="flex flex-wrap gap-1.5"
              >
                {(Object.keys(FORMS) as FormKey[]).map((k) => {
                  const active = recipe.p.pattern === k
                  return (
                    <Tooltip
                      key={k}
                      title={FORMS[k].label}
                      description={FORM_HELP[k]}
                      placement="left"
                      arrow
                      delay={400}
                    >
                      <Button
                        type="button"
                        size="sm"
                        color={active ? "secondary" : "tertiary"}
                        aria-pressed={active}
                        onClick={() => setP("pattern", k)}
                      >
                        {FORMS[k].label}
                      </Button>
                    </Tooltip>
                  )
                })}
              </div>
              <RecipeSlider
                paramKey="busy"
                label="How busy"
                value={recipe.p.busy}
                onChange={(v) => setP("busy", v)}
                help={SLIDER_HELP.busy}
              />
              <RecipeSlider
                paramKey="molten"
                label="How molten"
                value={recipe.p.molten}
                onChange={(v) => setP("molten", v)}
                help={SLIDER_HELP.molten}
              />
              <RecipeSlider
                paramKey="drift"
                label="Drift"
                value={recipe.p.drift}
                onChange={(v) => setP("drift", v)}
                help={SLIDER_HELP.drift}
              />
              <RecipeSlider
                paramKey="focus"
                label="Focal pull"
                value={recipe.p.focus}
                onChange={(v) => setP("focus", v)}
                help={SLIDER_HELP.focus}
              />
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1.5">
                  <span className="text-secondary text-sm font-medium">
                    Focal point
                  </span>
                  <HelpTip label="Focal point" help={SECTION_HELP.focal} />
                </span>
                <div
                  role="group"
                  aria-label="Focal point"
                  className="grid grid-cols-3 gap-1"
                >
                  {FOCAL_LABELS.map((label, i) => {
                    const active = recipe.p.focal === i
                    return (
                      <button
                        key={label}
                        type="button"
                        aria-label={`Focal point ${label}`}
                        aria-pressed={active}
                        onClick={() => setP("focal", i)}
                        className={[
                          "outline-focus-ring ring-primary size-5 rounded-sm ring-1 transition-colors duration-100 ring-inset focus-visible:outline-2 focus-visible:outline-offset-2",
                          active
                            ? "bg-brand-solid"
                            : "bg-primary hover:bg-primary_hover",
                        ].join(" ")}
                      />
                    )
                  })}
                </div>
              </div>
            </section>

            <section className="border-secondary flex flex-col gap-4 border-t pt-5">
              <HelpHeading title="Impression" help={SECTION_HELP.impression} />
              <div
                role="group"
                aria-label="Screening"
                className="flex flex-wrap gap-1.5"
              >
                {(Object.keys(SCREENS) as ScreenKey[]).map((k) => {
                  const active = recipe.p.screen === k
                  return (
                    <Tooltip
                      key={k}
                      title={SCREENS[k].label}
                      description={SCREEN_HELP[k]}
                      placement="left"
                      arrow
                      delay={400}
                    >
                      <Button
                        type="button"
                        size="sm"
                        color={active ? "secondary" : "tertiary"}
                        aria-pressed={active}
                        onClick={() => setP("screen", k)}
                      >
                        {SCREENS[k].label}
                      </Button>
                    </Tooltip>
                  )
                })}
              </div>
              <RecipeSlider
                paramKey="worn"
                label="How worn"
                value={recipe.p.worn}
                onChange={(v) => setP("worn", v)}
                help={SLIDER_HELP.worn}
              />
              <RecipeSlider
                paramKey="lanes"
                label="Roller lanes"
                value={recipe.p.lanes}
                onChange={(v) => setP("lanes", v)}
                help={SLIDER_HELP.lanes}
              />
              <RecipeSlider
                paramKey="laneCover"
                label="Ink coverage"
                value={recipe.p.laneCover}
                onChange={(v) => setP("laneCover", v)}
                help={SLIDER_HELP.laneCover}
              />
              <RecipeSlider
                paramKey="laneAngle"
                label="Roller angle"
                value={recipe.p.laneAngle}
                onChange={(v) => setP("laneAngle", v)}
                format={(v) => `${v}°`}
                help={SLIDER_HELP.laneAngle}
              />
              <RecipeSlider
                paramKey="register"
                label="Registration"
                value={recipe.p.register}
                onChange={(v) => setP("register", v)}
                help={SLIDER_HELP.register}
              />
            </section>

            <section className="border-secondary flex flex-col gap-4 border-t pt-5">
              <HelpHeading title="Paper" help={SECTION_HELP.paper} />
              <RecipeSlider
                paramKey="grain"
                label="Paper tooth"
                value={recipe.p.grain}
                onChange={(v) => setP("grain", v)}
                help={SLIDER_HELP.grain}
              />
            </section>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}

/** Paper tile with the ink stack as offset dots — the colourway at a glance. */
function InkSwatch({ paper, inks }: { paper: string; inks: string[] }) {
  return (
    <span
      className="ring-primary relative size-8 shrink-0 rounded ring-1 ring-inset"
      style={{ background: paper }}
      aria-hidden
    >
      {inks.map((ink, i) => (
        <span
          key={ink}
          className="absolute top-1/2 size-3 -translate-y-1/2 rounded-full"
          style={{
            left: 4 + i * 8,
            background: ink,
            boxShadow: `0 0 0 1.5px ${paper}`,
          }}
        />
      ))}
    </span>
  )
}
